import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { onSettingsChanged, SETTINGS_SCOPES, type SettingsScope } from '@services/settingsSync'

/**
 * React's half of cross-device settings sync. See services/settingsSync for the transport.
 *
 * Two hooks, because settings screens on this site come in two shapes and they want opposite
 * things from an incoming change.
 */

/**
 * Query keys a scope makes stale, for the screens built on React Query.
 *
 * Deliberately generous: a scope names an area, so it invalidates everything that reads that area
 * rather than the one key that definitely moved. An invalidation is one conditional refetch of data
 * that is already cached, and being too narrow here fails invisibly — a screen that never updates,
 * with nothing to indicate why.
 */
const QUERY_KEYS: Record<SettingsScope, string[][]> = {
  [SETTINGS_SCOPES.account]: [['user', 'account'], ['preferences']],
  // The stat visibility flags live on the profile, and the boards report whether the caller is on
  // them, so a profile change can move a leaderboard the person is looking at.
  [SETTINGS_SCOPES.profile]: [['profile'], ['user', 'profile'], ['leaderboard', 'stats']],
  [SETTINGS_SCOPES.notifications]: [['notifications', 'settings']],
  [SETTINGS_SCOPES.journalSound]: [['journalSound']],
  [SETTINGS_SCOPES.adultContent]: [['adultContent'], ['adventures']],
  [SETTINGS_SCOPES.privacy]: [['privacy', 'settings'], ['privacySettings']],
  [SETTINGS_SCOPES.security]: [['security'], ['smsQuickLogin'], ['passwordlessPhoneLogin']],
  [SETTINGS_SCOPES.sessions]: [['sessions'], ['trustedDevices']],
  [SETTINGS_SCOPES.homeLocation]: [['homeLocation']],
  // Base Camp re-resolves its layout from this query; an arrangement being edited is a separate draft,
  // so a change from another device never lands under the pointer.
  [SETTINGS_SCOPES.dashboardLayout]: [['dashboardLayouts']],
  [SETTINGS_SCOPES.editor]: [['editorPreferences']],
}

/**
 * Mount once, high in the tree. Keeps cached settings honest everywhere they are read.
 *
 * This is the whole story for anything that only *displays* a setting — the account menu's presence
 * dot, a screen that reads units, the leaderboard's "you are on this board". Those have no form to
 * protect, so the right response to a change elsewhere is simply to refetch.
 */
export function useSettingsSyncInvalidation() {
  const queryClient = useQueryClient()

  useEffect(
    () =>
      onSettingsChanged((scope) => {
        for (const key of QUERY_KEYS[scope] ?? []) {
          queryClient.invalidateQueries({ queryKey: key })
        }
      }),
    [queryClient]
  )
}

/**
 * For a settings section that holds a form.
 *
 * A form cannot simply reload. Somebody may be part-way through typing, and replacing what they
 * have written with what another device just saved loses work they were not told they could lose —
 * from an event they never asked for and did not see coming.
 *
 * So the caller says whether it is safe:
 *
 * - `reload` runs when the section is clean, and the change lands silently. This is the common
 *   case, and the one that makes the feature feel like the two screens are the same screen.
 * - When it is dirty, nothing is touched and `changedElsewhere` goes true, so the section can offer
 *   the choice rather than make it. Calling `dismiss` clears the flag, for a section that has just
 *   reloaded on the person's say-so or decided to keep what it has.
 *
 * @param scopes Which areas this section reads. An event for anything else is ignored.
 * @param reload What to run when it is safe to take the change.
 * @param isDirty Whether there are unsaved edits right now. Read at event time, not at render time.
 */
export function useSettingsSection(
  scopes: SettingsScope[],
  reload: () => void,
  isDirty: () => boolean = () => false
) {
  const [changedElsewhere, setChangedElsewhere] = useState(false)

  // Through refs so the subscription is not torn down and rebuilt on every render. A section that
  // rebuilt it each time would drop events fired in the gap, which is exactly when they arrive:
  // right after a save, while the component is re-rendering.
  const reloadRef = useRef(reload)
  const isDirtyRef = useRef(isDirty)

  // Assigned in an effect with no dependency list rather than during render. Both are the same
  // "keep the latest closure" idiom, but writing a ref during render is a lie about rendering
  // being side-effect free, and the mobile twin's lint rules catch it as one.
  useEffect(() => {
    reloadRef.current = reload
    isDirtyRef.current = isDirty
  })

  const scopeKey = scopes.join(',')

  useEffect(
    () =>
      onSettingsChanged((scope) => {
        if (!scopeKey.split(',').includes(scope)) return

        if (isDirtyRef.current()) {
          setChangedElsewhere(true)
          return
        }
        reloadRef.current()
      }),
    [scopeKey]
  )

  const dismiss = useCallback(() => setChangedElsewhere(false), [])

  return { changedElsewhere, dismiss }
}
